/**
 * Regles d'autorisation, partagees client/serveur.
 *
 * Les gardes serveur (`server/utils/auth.ts`) et le composable client
 * (`app/composables/useAuthorization.ts`) s'appuient tous deux sur ces
 * fonctions : une seule definition, donc aucun risque de voir l'interface
 * masquer un bouton que l'API autoriserait encore, ou l'inverse.
 *
 * Roles applicatifs de l'app Adherence, catalogue `app_roles` du SSO :
 * admin, agent, viewer. Le SSO renvoie `role: appRole || user.role` — sans
 * attribution sur cette application, `role` reprend le role global
 * (superadmin, admin, user). Les listes ci-dessous sont donc des listes
 * blanches : un role inconnu, ou un `user` sans attribution, tombe du cote
 * refuse, jamais l'inverse.
 */

/** Import et exports de la base adherents. */
export const ADMIN_ROLES = ['admin', 'superadmin']

/** Export RNIC : donnees publiques, ouvertes aux agents mais pas aux lecteurs. */
export const NON_VIEWER_ROLES = ['agent', ...ADMIN_ROLES]

/**
 * Roles ouvrant l'application. Un utilisateur sans role applicatif n'y entre
 * pas : le SSO recopie alors son role global dans `role`, valeur hors
 * catalogue qui ne figure pas dans cette liste.
 */
export const APP_ROLES = ['viewer', ...NON_VIEWER_ROLES]

/** Les deux champs de role renvoyes par le SSO, seuls necessaires ici. */
export interface RoleBearer {
  role: string
  global_role: string
}

/** Un superadmin global garde ses droits meme si l'app lui attribue un role moindre. */
export function isGlobalSuperAdmin(user: RoleBearer | null | undefined): boolean {
  return user?.global_role === 'superadmin'
}

/**
 * Droit d'entrer dans l'application, meme en simple consultation.
 *
 * Tant qu'aucun role applicatif n'a ete attribue dans le SSO, l'acces est
 * refuse : mieux vaut un utilisateur bloque qui reclame son role qu'un
 * utilisateur qui consulte des donnees sans qu'on le lui ait accorde.
 */
export function canAccessApp(user: RoleBearer | null | undefined): boolean {
  if (!user) return false
  return APP_ROLES.includes(user.role) || isGlobalSuperAdmin(user)
}

/** Droit d'importer et d'exporter la base adherents. */
export function canManageData(user: RoleBearer | null | undefined): boolean {
  if (!user) return false
  return ADMIN_ROLES.includes(user.role) || isGlobalSuperAdmin(user)
}

/** Droit d'exporter le registre RNIC. */
export function canExportRnicData(user: RoleBearer | null | undefined): boolean {
  if (!user) return false
  return NON_VIEWER_ROLES.includes(user.role) || isGlobalSuperAdmin(user)
}

/**
 * Droit de creer, modifier et supprimer des donnees metier : adherents,
 * adhesions, paiements, coproprietes.
 *
 * Meme perimetre que `canExportRnicData` aujourd'hui, mais intention
 * distincte : restreindre un jour l'export RNIC ne doit pas fermer les
 * ecritures au passage, ni l'inverse.
 */
export function canWriteData(user: RoleBearer | null | undefined): boolean {
  if (!user) return false
  return NON_VIEWER_ROLES.includes(user.role) || isGlobalSuperAdmin(user)
}
